import { useCallback, useEffect, useRef, useState } from 'react';
import apiService, { ApiError } from '@/services/apiService';
import { sortQuotesByPremiumRate } from '@/services/utils';
import type { ProductTypeCoveragePayload, QuoteItem, QuoteListResponse } from '@/types/app';

const INITIAL_DELAY_MS = 1000;
const POLL_INTERVAL_MS = 1000;
const MAX_POLL_ATTEMPTS = 40;
// When both the PATCH trigger and the fallback GET return 400/422 the form is
// still transitioning state. Retry silently before surfacing an error.
const NOT_READY_RETRY_LIMIT = 3;
// Once status is terminal but the backend still expects more quotes than have
// arrived, give insurers a small window to land late results before accepting
// the current state. Without this cap, an insurer that returns zero quotes for
// a product would force polling until MAX_POLL_ATTEMPTS exhausted (~40s) and
// surface a misleading "took too long" error.
const POST_TERMINAL_POLL_LIMIT = 3;
const PROCESSING_STATUS = 'PROCESSING_QUOTES';

interface UseProductQuotesResult {
	quotes: QuoteItem[];
	expectedNumQuotes: number | null;
	isLoading: boolean;
	error: string | null;
	refetch: (coverage?: ProductTypeCoveragePayload) => void;
	startLoading: () => void;
}

/**
 * Triggers and streams quotes for a specific product type via the async API.
 *
 * On mount the hook does a **GET first**: if a fetch is already in flight
 * (`PROCESSING_QUOTES`) it opens an SSE stream and renders quotes as they land;
 * if quotes are already complete it renders them without streaming. Otherwise it
 * PATCHes to schedule retrieval and then streams. A coverage change (`refetch`)
 * always PATCHes. If the SSE connection fails, the hook falls back to the legacy
 * GET poll loop so behaviour degrades gracefully.
 */
const useProductQuotes = (
	id: string,
	productType: string,
	initialCoverage?: ProductTypeCoveragePayload
): UseProductQuotesResult => {
	const [quotes, setQuotes] = useState<QuoteItem[]>([]);
	const [expectedNumQuotes, setExpectedNumQuotes] = useState<number | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const coverageRef = useRef<ProductTypeCoveragePayload | undefined>(initialCoverage);
	const isMountedRef = useRef(true);
	const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const streamAbortRef = useRef<AbortController | null>(null);
	const triggerCountRef = useRef(0);

	// Stable ref so teardown never needs to be listed as a dependency.
	const teardownRef = useRef(() => {
		if (pollTimerRef.current) {
			clearTimeout(pollTimerRef.current);
			pollTimerRef.current = null;
		}
		if (streamAbortRef.current) {
			streamAbortRef.current.abort();
			streamAbortRef.current = null;
		}
	});

	const runFetch = useCallback(
		async (
			coverage: ProductTypeCoveragePayload | undefined,
			triggerNum: number,
			forceTrigger: boolean,
			notReadyRetries = 0
		) => {
			if (!isMountedRef.current) return;

			teardownRef.current();
			setIsLoading(true);
			setError(null);
			setQuotes([]);
			setExpectedNumQuotes(null);

			const stale = () => !isMountedRef.current || triggerNum !== triggerCountRef.current;
			const extractQuotes = (rawQuotes: Record<string, QuoteItem[]>): QuoteItem[] =>
				sortQuotesByPremiumRate(rawQuotes[productType] ?? []);
			const applyResult = (result: QuoteListResponse) => {
				setExpectedNumQuotes(result.expected_num_quotes);
				setQuotes(extractQuotes(result.quotes));
			};

			// Legacy GET poll loop — the fallback when SSE is unavailable.
			const pollQuotes = () => {
				let attempts = 0;
				let postTerminalPolls = 0;
				let hasReceivedQuotes = false;

				const poll = async () => {
					if (stale()) return;
					if (attempts >= MAX_POLL_ATTEMPTS) {
						if (!hasReceivedQuotes) {
							setError('Het ophalen van offertes heeft te lang geduurd. Probeer het opnieuw.');
						}
						setIsLoading(false);
						return;
					}

					attempts += 1;

					try {
						const pollResult = await apiService.quote.byProductType.fetch(id, productType);
						if (stale()) return;

						setExpectedNumQuotes(pollResult.expected_num_quotes);
						const currentQuotes = extractQuotes(pollResult.quotes);
						if (currentQuotes.length > 0) {
							setQuotes(currentQuotes);
							hasReceivedQuotes = true;
						}

						const stillProcessing = pollResult.status === PROCESSING_STATUS;
						const stillWaitingForQuotes =
							pollResult.expected_num_quotes > 0 &&
							currentQuotes.length < pollResult.expected_num_quotes;

						if (stillProcessing) {
							postTerminalPolls = 0;
							pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
						} else if (stillWaitingForQuotes && postTerminalPolls < POST_TERMINAL_POLL_LIMIT) {
							postTerminalPolls += 1;
							pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
						} else {
							setIsLoading(false);
						}
					} catch (pollErr) {
						if (stale()) return;
						// A 400/422 during polling means the form transitioned out of the
						// quote-retrieval state. Keep whatever quotes are already showing.
						if (pollErr instanceof ApiError && (pollErr.status === 400 || pollErr.status === 422)) {
							setIsLoading(false);
							return;
						}
						const msg =
							pollErr instanceof Error ? pollErr.message : 'Fout bij ophalen van offertes.';
						setError(msg);
						setIsLoading(false);
					}
				};

				pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
			};

			// Live SSE stream — the primary path while a fetch is in flight.
			const streamQuotes = () => {
				const controller = new AbortController();
				streamAbortRef.current = controller;
				let doneReceived = false;

				apiService.quote.byProductType
					.stream(
						id,
						productType,
						{
							onQuotes: (data) => {
								if (stale()) return;
								applyResult(data);
							},
							onDone: () => {
								doneReceived = true;
								if (!stale()) setIsLoading(false);
								controller.abort(); // close the long-lived SSE connection
							}
						},
						controller.signal
					)
					.then(
						() => {
							// Stream closed *without* a `done` event — e.g. a dropped connection
							// (proxy idle-timeout, server restart). Finalize via the poll loop so
							// the skeleton never hangs.
							if (!doneReceived && !controller.signal.aborted && !stale()) pollQuotes();
						},
						() => {
							if (doneReceived || controller.signal.aborted || stale()) return;
							// SSE failed before completion — degrade to the poll loop.
							pollQuotes();
						}
					);
			};

			try {
				// GET-first: on mount, only stream when a fetch is actually in flight;
				// render directly when quotes are already complete.
				if (!forceTrigger) {
					let snapshot: QuoteListResponse | null = null;
					try {
						snapshot = await apiService.quote.byProductType.fetch(id, productType);
					} catch (fetchErr) {
						// Only a 400/422 means "form not ready for a GET yet" — fall through to
						// the trigger path. Surface anything else (transient 500, network drop)
						// instead of silently converting it into a state-mutating PATCH.
						if (
							!(
								fetchErr instanceof ApiError &&
								(fetchErr.status === 400 || fetchErr.status === 422)
							)
						) {
							throw fetchErr;
						}
					}
					if (stale()) return;

					if (snapshot) {
						if (snapshot.status === PROCESSING_STATUS) {
							applyResult(snapshot);
							streamQuotes();
							return;
						}
						if (extractQuotes(snapshot.quotes).length > 0) {
							applyResult(snapshot);
							setIsLoading(false);
							return;
						}
					}
				}

				// Schedule (or re-schedule, on coverage change) a background fetch.
				const triggerResult = await apiService.quote.byProductType.trigger(
					id,
					productType,
					coverage
				);
				if (stale()) return;

				if (triggerResult.status !== PROCESSING_STATUS) {
					// Backend short-circuited (quotes already exist). PATCH only returns
					// status, so we still need a GET to pick up the cached quotes.
					const cached = await apiService.quote.byProductType.fetch(id, productType);
					if (stale()) return;
					applyResult(cached);
					setIsLoading(false);
					return;
				}

				streamQuotes();
			} catch (triggerErr) {
				if (stale()) return;

				// A 400/422 from the PATCH trigger means the form is in a state where a
				// new quote run cannot be started (e.g. already in PENDING_CUSTOMER_SELECTION
				// after the user navigates back). Fall back to a GET to show cached quotes.
				if (
					triggerErr instanceof ApiError &&
					(triggerErr.status === 400 || triggerErr.status === 422)
				) {
					try {
						const fallback = await apiService.quote.byProductType.fetch(id, productType);
						if (stale()) return;
						applyResult(fallback);
						// A fetch can still be in flight (e.g. the form was already processing
						// when the PATCH was rejected) — follow the same stream path the
						// GET-first branch uses rather than stopping with partial quotes.
						if (fallback.status === PROCESSING_STATUS) {
							streamQuotes();
							return;
						}
						setIsLoading(false);
						return;
					} catch (fallbackErr) {
						if (stale()) return;
						// Both PATCH and GET returned 400/422: the form state is still
						// transitioning. Retry silently rather than surfacing an error.
						if (
							fallbackErr instanceof ApiError &&
							(fallbackErr.status === 400 || fallbackErr.status === 422) &&
							notReadyRetries < NOT_READY_RETRY_LIMIT
						) {
							pollTimerRef.current = setTimeout(() => {
								if (isMountedRef.current && triggerNum === triggerCountRef.current) {
									runFetch(coverage, triggerNum, forceTrigger, notReadyRetries + 1);
								}
							}, POLL_INTERVAL_MS);
							return;
						}
					}
				}

				const msg =
					triggerErr instanceof Error ? triggerErr.message : 'Fout bij ophalen van offertes.';
				setError(msg);
				setIsLoading(false);
			}
		},
		[id, productType]
	);

	const refetch = useCallback(
		(coverage?: ProductTypeCoveragePayload) => {
			teardownRef.current();
			coverageRef.current = coverage;
			triggerCountRef.current += 1;
			// A coverage change must always re-trigger (skip the GET-first short-circuit).
			runFetch(coverage, triggerCountRef.current, true);
		},
		[runFetch]
	);

	// Immediately shows the loading skeleton state without firing an API call.
	// Use this to give instant visual feedback before a save + refetch sequence.
	const startLoading = useCallback(() => {
		teardownRef.current();
		triggerCountRef.current += 1;
		setIsLoading(true);
		setQuotes([]);
		setExpectedNumQuotes(null);
		setError(null);
	}, []);

	useEffect(() => {
		isMountedRef.current = true;
		triggerCountRef.current += 1;
		const triggerNum = triggerCountRef.current;

		// Give the backend a moment to update the form status before the first
		// call. Without this delay the GET/PATCH sometimes returns 400/422
		// ("not in the correct state") immediately after navigating to the page.
		const initTimer = setTimeout(() => {
			runFetch(coverageRef.current, triggerNum, false);
		}, INITIAL_DELAY_MS);

		const teardown = teardownRef.current;
		return () => {
			isMountedRef.current = false;
			clearTimeout(initTimer);
			teardown();
		};
	}, [runFetch]);

	return { quotes, expectedNumQuotes, isLoading, error, refetch, startLoading };
};

export default useProductQuotes;
